import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { Download, X, Share } from "lucide-react";

// PWA install-prompt state is *non-sensitive* UX preference only.
// Never store auth tokens or PII in this key.
const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 7;
const IOS_HINT_DELAY_MS = 4000;
const DAY_MS = 1000 * 60 * 60 * 24;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    return (Date.now() - ts) / DAY_MS < DISMISS_DAYS;
  } catch {
    return false;
  }
}

function storeDismissal() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[PWA] localStorage unavailable", err);
    }
  }
}

export default function PWAInstallPrompt() {
  const { lang } = useI18n();
  const rtl = lang === "ar";
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari doesn't fire beforeinstallprompt — show a manual hint.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, IOS_HINT_DELAY_MS);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    storeDismissal();
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);
    setVisible(false);
    if (choice?.outcome !== "accepted") storeDismissal();
  };

  if (!visible) return null;

  const t = rtl
    ? {
        title: "ثبّت التطبيق على جهازك",
        desc: "اضغط زر التثبيت وستحصل على أيقونة عبدالله | علم القهوة على شاشة جهازك — يعمل بدون متصفح.",
        install: "تثبيت",
        later: "لاحقاً",
        iosTitle: "ثبّت التطبيق على iPhone",
        iosDesc: "اضغط زر المشاركة في Safari ثم اختر «إضافة إلى الشاشة الرئيسية».",
      }
    : {
        title: "Install the app",
        desc: "Add Abdullah | Coffee Science to your device — opens like a real app, no browser bar.",
        install: "Install",
        later: "Later",
        iosTitle: "Install on iPhone",
        iosDesc: "Tap the Share button in Safari, then choose “Add to Home Screen”.",
      };

  const heading = iosHint ? t.iosTitle : t.title;
  const description = iosHint ? t.iosDesc : t.desc;

  return (
    <div
      data-testid="pwa-install-prompt"
      dir={rtl ? "rtl" : "ltr"}
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[9998] rounded-2xl shadow-2xl border bg-[#2A2421] text-[#F9F6F0] p-4 sm:p-5 backdrop-blur"
      style={{ borderColor: "rgba(249,246,240,0.12)" }}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(199,97,64,0.18)", color: "#C76140" }}
          aria-hidden
        >
          {iosHint ? <Share size={20} /> : <Download size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm sm:text-base font-semibold leading-tight">
            {heading}
          </p>
          <p className="text-xs sm:text-sm opacity-80 mt-1 leading-relaxed">
            {description}
          </p>
          {!iosHint && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={install}
                data-testid="pwa-install-button"
                className="px-3 py-1.5 rounded-full text-sm font-semibold text-white transition-transform active:scale-95"
                style={{ background: "#C76140" }}
              >
                {t.install}
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                className="px-3 py-1.5 rounded-full text-sm opacity-80 hover:opacity-100"
              >
                {t.later}
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          data-testid="pwa-install-close"
          className="shrink-0 -m-1 p-1 opacity-60 hover:opacity-100"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
